import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useHospitalSimilarity } from './hooks/useHospitalSimilarity';
import { useBatchList, BatchSummary } from './hooks/useBatchList';
import StatusCard from './components/StatusCard';
import Filters from './components/Filters';
import HospitalCard from './components/HospitalCard';
import Pagination from './components/Pagination';
import BatchList from './components/BatchList';
import LoadingState from './components/common/LoadingState';
import EmptyState from './components/common/EmptyState';
import { STATUS_MAPPINGS } from './constants/statusMappings';
import { applyInsuranceForBatch, uploadManualHospitalCsv } from '../../Http/Index';
import './styles/HospitalSimilarity.css';

const HospitalSimilarity: React.FC = () => {
  const location = useLocation();

  // ── Batch selection state ──────────────────────────────────────────────────
  const [activeBatch, setActiveBatch] = useState<BatchSummary | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState<{ success: boolean; message: string } | null>(null);

  // ── Upload panel state ─────────────────────────────────────────────────────
  const [uploadInsurance, setUploadInsurance] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // ── Hooks ──────────────────────────────────────────────────────────────────
  const {
    hospitals, statusCounts, filters, currentPage, totalPages, loading,
    expandedCards, selectedMatches,
    updateHospitalStatus, updateHospitalField, reprocessHospital,
    toggleCardExpansion, updateFilters, changePage, setSelectedMatch,
  } = useHospitalSimilarity();

  const { batches, page: batchPage, setPage: setBatchPage, totalPages: batchTotalPages, loading: batchLoading, reload: reloadBatches } = useBatchList();

  // ── Tab reset: clicking "Hospital Similarity" tab while already on it ──────
  const locationState = location.state as { reset?: number } | null;
  const resetKey = locationState?.reset;
  useEffect(() => {
    if (resetKey) {
      setActiveBatch(null);
      setApplyResult(null);
    }
  }, [resetKey]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleStatusCardClick = useCallback((status: string) => {
    const mappedStatus = STATUS_MAPPINGS[status as keyof typeof STATUS_MAPPINGS] || status;
    updateFilters({ status: mappedStatus });
  }, [updateFilters]);

  const handleSelectBatch = useCallback((batch: BatchSummary) => {
    setActiveBatch(batch);
    setApplyResult(null);
    updateFilters({ jobId: batch.jobId, status: 'all', search: '' });
  }, [updateFilters]);

  const handleClearBatch = useCallback(() => {
    setActiveBatch(null);
    setApplyResult(null);
    updateFilters({ jobId: undefined, status: 'all', search: '' });
  }, [updateFilters]);

  const handleUpload = useCallback(async () => {
    if (!uploadInsurance.trim()) { setUploadError('Insurance name is required'); return; }
    if (!uploadFile) { setUploadError('Please select a CSV file'); return; }
    setUploading(true);
    setUploadError(null);
    try {
      const res = await uploadManualHospitalCsv(uploadFile, uploadInsurance.trim());
      const ins = uploadInsurance.trim();
      setUploadInsurance('');
      setUploadFile(null);
      if (fileRef.current) fileRef.current.value = '';
      reloadBatches();
      handleSelectBatch({
        jobId: res.data.jobId,
        insurance: ins,
        source: 'manual',
        createdAt: new Date().toISOString(),
        total: res.data.totalRows,
        formatted: res.data.totalRows,
        matched: 0,
        doubtful: 0,
        new_hospital: 0,
        approved: 0,
        rejected: 0,
        pending: res.data.totalRows,
      });
    } catch (err: any) {
      setUploadError(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [uploadInsurance, uploadFile, reloadBatches, handleSelectBatch]);

  const handleApply = useCallback(async () => {
    if (!activeBatch) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const res = await applyInsuranceForBatch(activeBatch.jobId);
      setApplyResult({ success: true, message: res.data.message });
      reloadBatches();
    } catch (err: any) {
      setApplyResult({
        success: false,
        message: err?.response?.data?.message || 'Failed to apply insurance reconciliation',
      });
    } finally {
      setApplying(false);
      setShowConfirm(false);
    }
  }, [activeBatch, reloadBatches]);

  const canApply = activeBatch
    ? activeBatch.pending === 0 && activeBatch.new_hospital === 0
    : false;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="hospital-similarity-container">
      <div className="hospital-similarity-content">
        <div className="hospital-similarity-header">
          <div className="header-content">
            <h1 className="hospital-similarity-title">Hospital Review Dashboard</h1>
          </div>
        </div>

        {/* ── No batch selected: upload panel + batch list ── */}
        {!activeBatch && (
          <>
            <div className="manual-upload-panel">
              <div className="manual-upload-row">
                <div className="manual-upload-field">
                  <label>Insurance Name</label>
                  <input
                    type="text"
                    placeholder="e.g. StarHealth"
                    value={uploadInsurance}
                    onChange={e => { setUploadInsurance(e.target.value); setUploadError(null); }}
                  />
                </div>
                <div className="manual-upload-field">
                  <label>
                    CSV File{' '}
                    <span style={{ color: '#95a5a6', fontWeight: 400 }}>(name, address columns)</span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="file"
                      accept=".csv"
                      ref={fileRef}
                      style={{ display: 'none' }}
                      onChange={e => { setUploadFile(e.target.files?.[0] || null); setUploadError(null); }}
                    />
                    <button
                      type="button"
                      className="btn-pagination"
                      style={{ padding: '6px 14px', fontSize: 13 }}
                      onClick={() => fileRef.current?.click()}
                    >
                      📂 {uploadFile ? uploadFile.name : 'Choose CSV'}
                    </button>
                    {uploadFile && (
                      <span style={{ fontSize: 12, color: '#7f8c8d' }}>
                        {(uploadFile.size / 1024).toFixed(1)} KB
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="btn-pagination"
                  style={{ background: '#27ae60', marginTop: 20 }}
                  disabled={uploading}
                  onClick={handleUpload}
                >
                  {uploading ? 'Uploading...' : 'Upload & Process'}
                </button>
              </div>
              {uploadError && (
                <div style={{ color: '#e74c3c', fontSize: 13, marginTop: 8 }}>{uploadError}</div>
              )}
            </div>

            <BatchList
              batches={batches}
              loading={batchLoading}
              page={batchPage}
              totalPages={batchTotalPages}
              onPageChange={(p) => { setBatchPage(p); reloadBatches(p); }}
              onSelect={handleSelectBatch}
            />
          </>
        )}

        {/* ── Batch selected: banner + hospital cards ── */}
        {activeBatch && (
          <>
            {/* Banner */}
            <div className="active-batch-banner">
              <div className="active-batch-info">
                <span className="active-batch-label">Batch:</span>
                <span>{activeBatch.insurance}</span>
                <span style={{ color: '#7f8c8d', fontSize: 12 }}>
                  · {activeBatch.matched + activeBatch.approved} matched
                  {activeBatch.pending > 0 && (
                    <span style={{ color: '#e67e22', marginLeft: 8 }}>
                      · {activeBatch.pending} pending review
                    </span>
                  )}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button
                  className={`btn-apply-insurance${canApply ? '' : ' btn-apply-insurance--disabled'}`}
                  disabled={!canApply || applying}
                  onClick={() => setShowConfirm(true)}
                  title={
                    canApply
                      ? 'Remove unmatched insurance'
                      : [
                        activeBatch.formatted > 0 ? `${activeBatch.formatted} formatted (pending review)` : '',
                        activeBatch.doubtful > 0 ? `${activeBatch.doubtful} doubtful (needs decision)` : '',
                        activeBatch.new_hospital > 0 ? `${activeBatch.new_hospital} new hospital (must be actioned)` : '',
                      ].filter(Boolean).join('\n') + '\n\nResolve all to enable'
                  }
                >
                  {applying ? 'Applying...' : 'Remove Unmatched Insurance'}
                </button>
                <button className="btn-clear-batch" onClick={handleClearBatch}>
                  ← Back to Batches
                </button>
              </div>
            </div>

            {/* Apply result */}
            {applyResult && (
              <div className={`apply-result-banner apply-result-banner--${applyResult.success ? 'success' : 'error'}`}>
                {applyResult.message}
              </div>
            )}

            {/* Confirm dialog */}
            {showConfirm && (
              <div className="batch-confirm-overlay">
                <div className="batch-confirm-box">
                  <h3 className="batch-confirm-title">Apply Insurance Reconciliation</h3>
                  <p className="batch-confirm-body">
                    This will update <strong>{activeBatch.insurance}</strong> insurance across the hospital database:
                  </p>
                  <ul className="batch-confirm-list">
                    <li>
                      <span className="batch-confirm-add">✓ {activeBatch.matched + activeBatch.approved} hospitals</span>
                      {' '}will have this insurance added
                    </li>
                    <li>
                      <span className="batch-confirm-remove">✗ All other hospitals</span>
                      {' '}currently holding <strong>{activeBatch.insurance}</strong> will have it removed
                    </li>
                  </ul>
                  <p className="batch-confirm-warning">
                    ⚠ This cannot be undone. The insurance list will be fully replaced.
                  </p>
                  <div className="batch-confirm-actions">
                    <button className="btn-pagination" onClick={() => setShowConfirm(false)} disabled={applying}>
                      Cancel
                    </button>
                    <button className="btn-apply-insurance" onClick={handleApply} disabled={applying}>
                      {applying ? 'Applying...' : 'Yes, Apply'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Status cards */}
            <div className="status-dashboard">
              <StatusCard count={statusCounts.formatted} label="Pending Review" status="formatted" isActive={filters.status === 'formatted'} onClick={handleStatusCardClick} />
              <StatusCard count={statusCounts.doubtful} label="Doubtful" status="doubtful" isActive={filters.status === 'doubtful'} onClick={handleStatusCardClick} />
              <StatusCard count={statusCounts.newHospital} label="New Hospital" status="new_hospital" isActive={filters.status === 'new_hospital'} onClick={handleStatusCardClick} />
              <StatusCard count={statusCounts.matched} label="Matched" status="matched" isActive={filters.status === 'matched'} onClick={handleStatusCardClick} />
              <StatusCard count={statusCounts.approved} label="Approved" status="approved" isActive={filters.status === 'approved'} onClick={handleStatusCardClick} />
              <StatusCard count={statusCounts.rejected} label="Rejected" status="rejected" isActive={filters.status === 'rejected'} onClick={handleStatusCardClick} />
              <StatusCard count={statusCounts.total} label="Total" status="total" isActive={filters.status === 'all'} onClick={handleStatusCardClick} />
            </div>

            <Filters filters={filters} onFiltersChange={updateFilters} />

            <div className="hospital-list" role="main" aria-label="Hospital list">
              {loading ? (
                <LoadingState />
              ) : hospitals.length === 0 ? (
                <EmptyState />
              ) : (
                hospitals.map((hospital) => (
                  <HospitalCard
                    key={hospital._id}
                    hospital={hospital}
                    isExpanded={expandedCards.has(hospital._id)}
                    selectedMatchId={selectedMatches[hospital._id]}
                    onToggleExpansion={toggleCardExpansion}
                    onStatusUpdate={updateHospitalStatus}
                    onFieldUpdate={updateHospitalField}
                    onReprocess={reprocessHospital}
                    onSelectMatch={setSelectedMatch}
                  />
                ))
              )}
            </div>

            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={changePage} />
          </>
        )}
      </div>
    </div>
  );
};

export default HospitalSimilarity;
